import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Actions } from '@src/app/shared/backend/auth/Action';
import { PermissionService } from '@src/app/shared/services/permission.service';
import { TranslateService } from '@ngx-translate/core';
import { ItemsService, BrandsService, FilterRequestForCombo } from '@src/app/shared/backend/clients';
import { BaseListComponent } from '@src/app/shared/components/base-list/base-list.component';
import { TableSettingService } from '@src/app/shared/services/table.setting.service';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ToasterService } from '@src/app/shared/components/toaster/toaster.service';
import { ExportService } from '@src/app/shared/services/export.service';
import { FormGroup } from '@angular/forms';
import { FiltersPanelFieldOverrides, FiltersPanelChangeEvent, FiltersPanelDropdownLazyLoadEvent } from '@src/app/shared/components/filters-panel/filters-panel.models';
import { applyFiltersPanelChange } from '@src/app/shared/components/filters-panel/filters-panel.helper';
import { FriendlyBooleanPipe } from '@app/shared/pipes/friendlyBoolean.pipe';
import { ItemLocationPipe } from '@src/app/shared/pipes/itemLocation.pipe';
import { buildRemoteDropdownRequest, normalizeDropdownState } from '@src/app/shared/components/filters-panel/filters-panel-dropdown.helpers';
import { loadStoredPaginatorState, resetPaginatorStateToFirstPage } from '@app/shared/components/paginator/paginator-storage.helper';

@Component({
    selector: 'app-items',
    templateUrl: './items.component.html',
    styleUrl: './items.component.scss',
    host: {
        class: 'app-items'
    },
    standalone: false
})
export class ItemsComponent extends BaseListComponent implements OnInit, OnDestroy {

  private initialLoadDone = false;
  private readonly componentDestroyed$ = new Subject<void>();

  public readonly itemsTableName = TableSettingService.ItemsTableName;

  public filtersPanelFieldOverrides: FiltersPanelFieldOverrides = {};

  private friendlyBoolOptions: Array<{ id: string | number; text: string }> = [];
  private dispositionOptions: Array<{ id: string | number; text: string }> = [];

  exports: boolean = false;
  allowCreateItem: boolean = false;
  currentDate: Date = new Date();
  displayExportModalbox: boolean = false;
 
  // Dropdown: brands
  brands = {
    results: [],
    pageIndex: 1,
    totalPages: 1,
    totalCount: 0,
    hasPreviousPage: false,
    hasNextPage: false
  };

  constructor(private router: Router,
    public permissionService: PermissionService,
    public override tableSettingService: TableSettingService,
    private exportService: ExportService,
    private translateService: TranslateService,
    private toasterService: ToasterService,
    private itemsService: ItemsService,
    private brandService: BrandsService) { super(); }

  ngOnInit() {
    this.init();
  }

  private async init(): Promise<void> {
    this.restorePaginatorStateFromStorage();

    this.exports = await this.permissionService.hasActionPermissions(Actions.export_items);
    this.allowCreateItem = await this.permissionService.hasActionPermissions(Actions.create_items);

    // i18n propagation on lang change
    this.rebuildFriendlyBoolOptions();
    this.rebuildDispositionOptions();

    this.translateService.onLangChange
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(() => {
        this.rebuildFriendlyBoolOptions();
        this.rebuildDispositionOptions();
        this.rebuildFiltersPanelOverrides();
        this.rebuildSortPanelColumnLabels();
      });

    await this.tableSettingService.init();
    await super.setCache(this.itemsTableName);

    // Default sort for stable pagination + translated labels in sort dropdown
    this.initSortPanel({
      defaultSort: { key: 'productSku', direction: 'ASC' },
      labelResolver: (col) => {
        const translationKey = `TABLE.HEADER.${col.header}`;
        const translated = this.translateService.instant(translationKey);

        if (translated && translated !== translationKey) {
          return translated;
        }

        return col.header;
      }
    });

    this.rebuildSortPanelColumnLabels();
    this.rebuildFiltersPanelOverrides();

    // Fallback: if the panel doesn't restore anything, load unfiltered!!
    setTimeout(async () => {
      if (!this.initialLoadDone) {
        this.initialLoadDone = true;
        await this.get();
      }
    });
  }

  override async get() {
    this.filter.pageNumber = this.pageNumber;
    this.filter.pageSize = this.pageSize;

    this.applySortToFilterRequest();

    this.page = await firstValueFrom(this.itemsService.apiItemsFilterTablePost(this.filter));
    this.data = this.page.results;
  }

  private rebuildFriendlyBoolOptions() {
    const pipe = new FriendlyBooleanPipe(this.translateService);
    this.friendlyBoolOptions = pipe.values;
  }

  private rebuildDispositionOptions() {
    const pipe = new ItemLocationPipe(this.translateService);
    this.dispositionOptions = pipe.getList();
  }

  private rebuildFiltersPanelOverrides() {
    this.filtersPanelFieldOverrides = {
      isGenuine: {
        fieldLabel: 'TABLE.HEADER.Genuine',
        isMultiselect: true,
        options: this.friendlyBoolOptions ?? [],
        optionLabel: 'text',
        optionValue: 'id',
        searchMode: 'local',
        showSearchbox: false,
        showClear: true,
        compactUi: true
      },
      isCpo: {
        fieldLabel: 'TABLE.HEADER.CPO',
        isMultiselect: true,
        options: this.friendlyBoolOptions ?? [],
        optionLabel: 'text',
        optionValue: 'id',
        searchMode: 'local',
        showSearchbox: false,
        showClear: true,
        compactUi: true
      },
      disposition: {
        fieldLabel: 'TABLE.HEADER.Disposition',
        isMultiselect: true,
        options: this.dispositionOptions ?? [],
        optionLabel: 'text',
        optionValue: 'id',
        searchMode: 'local',
        showSearchbox: false,
        showClear: true,
        compactUi: true
      },
      'product.brandId': {
        fieldLabel: 'TABLE.HEADER.Brand',
        isMultiselect: true,

        options: this.brands.results ?? [],
        optionLabel: 'text',
        optionValue: 'id',

        placeholder: 'Select...',
        searchPlaceholder: 'Search...',
        searchMode: 'remote',

        showSearchbox: true,
        showClear: true,
        compactUi: true,

        pageSize: 10,
        totalCount: this.brands.totalCount ?? 0,
        totalPages: this.brands.totalPages ?? 1,
        pageIndex: this.brands.pageIndex ?? 1,
        hasPreviousPage: this.brands.hasPreviousPage ?? false,
        hasNextPage: this.brands.hasNextPage ?? false
      }
    };
  }

  private rebuildSortPanelColumnLabels(): void {
    const visibleColumns = Array.isArray(this.tableColumns) ? this.tableColumns : [];

    // Update labels without resetting currently selected sort
    const nextSortableColumns = visibleColumns
      .filter((col: any) => {
        const field = String(col?.field ?? '').trim();
        return (
          field.length > 0 &&
          field.toLowerCase() !== 'actions' &&
          col?.sortable !== false
        );
      })
      .map((col: any) => {
        const translationKey = `TABLE.HEADER.${col.header}`;
        const translated = this.translateService.instant(translationKey);

        return {
          key: col.field,
          label: translated && translated !== translationKey ? translated : col.header
        };
      });

    this.sortableColumns = nextSortableColumns;

    // clear active sort if points to col that is not available due to system settings update
    const allowedKeys = new Set(nextSortableColumns.map(col => col.key));
    if (this.sortPanelSort?.key && !allowedKeys.has(this.sortPanelSort.key)) {
      this.onSortPanelChange(null);
    }
  }

  override onPageChange(event: any): void {
    this.initialLoadDone = true;
    super.onPageChange(event);
  }

  protected override onCriteriaResetRequested(): void {
    resetPaginatorStateToFirstPage({
      tableName: this.itemsTableName,
      pageSize: this.pageSize,
    });
  }

  private restorePaginatorStateFromStorage(): void {
    const stored = loadStoredPaginatorState(this.itemsTableName);

    if (!stored) {
      return;
    }

    const storedPageNumber = stored.valuesByFieldKey.pageNumber;
    const storedPageSize = stored.valuesByFieldKey.pageSize;

    this.pageNumber = storedPageNumber;
    this.pageSize = storedPageSize;

    this.filter.pageNumber = storedPageNumber;
    this.filter.pageSize = storedPageSize;
  }

  async onFiltersPanelDropdownLazyLoad(evt: FiltersPanelDropdownLazyLoadEvent): Promise<void> {
      if (!evt) {
        return;
      }
  
      // fetch dropdowns remote data
      const handlers: Record<string, (e: FiltersPanelDropdownLazyLoadEvent) => Promise<void>> = {
        'product.brandId': (e) => this.loadBrandsCombo(e)
      };
  
      const handler = handlers[evt.fieldKey];
      if (!handler) {
        return;
      }
  
      await handler(evt);
    }

  async onFiltersPanelChange(evt: FiltersPanelChangeEvent): Promise<void> {
    // Important: pass all table cols (not only visible ones) to prevent hidden col filters from affecting results
    const currentFilters = this.filter.filters as any;

    const nextFilters = applyFiltersPanelChange(
      evt,
      currentFilters,
      this.allTableColumns
    );

    if (!nextFilters) {
      return;
    }

    this.filter.filters = nextFilters as any;

    const isApplyAction = evt.source === 'apply';
    const isClearAction = evt.source === 'clear';
    const shouldResetPaging = isApplyAction || isClearAction;

    if (shouldResetPaging) {
      const firstPageNumber = 1;

      this.pageNumber = firstPageNumber;
      this.filter.pageNumber = firstPageNumber;

      this.onCriteriaResetRequested();
    }

    this.initialLoadDone = true;
    await this.get();
  }

  goToItemDetails(event: any, itemId: string) {
    this.router.navigate([`/items/${itemId}`]);
  }

  goToCreateItemForm() {
    this.router.navigate(['/items/add']);
  }

  onOpenExportModalbox() {
    this.displayExportModalbox = true;
  }

  handleExportSubmit(form: FormGroup) {
    const range = form.value.dateRange;
    const fileName = form.value.exportName;
    const fileType = form.value.format;

    this.exportService.exportData(
      this.filter,
      [range?.[0], range?.[1] ?? new Date()],
      this.itemsService.apiItemsExportPost.bind(this.itemsService),
      {
        fileName,
        fileType,
        onSuccess: () => {
          const title = this.translateService.instant('EXPORT_SETTINGS.TOAST_SUCCESS_TITLE');
          const message = this.translateService.instant('EXPORT_SETTINGS.TOAST_SUCCESS_MESSAGE');
          this.toasterService.show('success', title, message);
        },
        onError: (error) => {
          const title = this.translateService.instant('EXPORT_SETTINGS.TOAST_ERROR_TITLE');
          const message = this.translateService.instant('EXPORT_SETTINGS.TOAST_ERROR_MESSAGE');
          this.toasterService.show('error', title, message);
          console.error('Export error:', error);
        }
      }
    );
  }

  private async loadBrandsCombo(evt: FiltersPanelDropdownLazyLoadEvent): Promise<void> {
      const requestPayload: FilterRequestForCombo = buildRemoteDropdownRequest({
        pageIndex: evt.pageIndex,
        pageSize: evt.pageSize,
        search: evt.search
      });
  
      const response = await firstValueFrom(
        this.brandService.apiBrandsFilterComboPost(requestPayload)
      );
  
      this.brands = normalizeDropdownState(response as any, evt.pageIndex) as any;
      this.rebuildFiltersPanelOverrides();
    }

  ngOnDestroy() {
    this.componentDestroyed$.next();
    this.componentDestroyed$.complete();
  }
}
